import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import Input from "../../../../../Components/Common/Input/Input";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";
import { useEffect, useRef, useState } from "react";
import Card from "../Card";
import FormField from "../FormField";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import ResidentialEstimateNote from "../ResidentialEstimateNote";
import Button from "../../../../../Components/Common/Button/Button";
import SearchableSelect from "../../../../../Components/Common/SearchableSelect/SearchableSelect";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";

interface Props {
  prefill?: ProgressReportPrefill;
  initialExtraFlags?: ExtraFlag[];
  onExtraFlagsChange?: (flags: ExtraFlag[]) => void;
  onFlagNarrativesChange?: (narratives: Record<string, string>) => void;
  onFlagIncludesChange?: (includes: Record<string, boolean>) => void;
  submitted?: boolean;
}

interface ExtraFlag {
  id: string;
  phase?: string;
  itemLabel: string;
  itemName?: string;
  itemCode?: number;
  cscEstimation: string;
  gcBilledPct?: number;
  gcCscText: string;
  narrative: string;
  isIncluded: boolean;
  index?: number | undefined;
  isCommercial?: boolean;
}

const FlagCard = ({
  phase,
  itemLabel,
  itemName,
  itemCode,
  gcBilledPct,
  cscEstimation,
  narrative,
  onNarrativeChange,
  isIncluded = true,
  onIncludedChange,
  error,
  index,
  isCommercial,
}: {
  phase?: string;
  itemLabel: string;
  itemName?: string;
  itemCode?: number;
  gcBilledPct?: number;
  cscEstimation?: string;
  narrative: string;
  onNarrativeChange?: (val: string) => void;
  isIncluded?: boolean;
  onIncludedChange?: (val: boolean) => void;
  error?: string;
  index?: number;
  isCommercial?: boolean;
}) => {
  const discrepancy =
    gcBilledPct !== undefined && cscEstimation !== undefined
      ? Math.round(gcBilledPct - parseFloat(cscEstimation))
      : null;
  return (
    <div className="flagItem">
      <div className="flagItem__head">
        <div className="flagItem__title">
          <span className="flagIcon">⚠</span>
          Flag {index !== undefined ? `${index + 1} — ` : ""}
          {isCommercial ? (
            <>
              {itemName && (
                <span className="itemName">
                  {itemName} (Line {itemCode}){" "}
                </span>
              )}
            </>
          ) : (
            <>
              {phase ? `Phase ${phase}` : ""}
              {itemLabel && <span className="itemName"> ({itemLabel})</span>}
              {itemName && <span className="itemName"> ({itemName})</span>}
            </>
          )}
        </div>
        <label className="include">
          <Input
            type="checkbox"
            checked={isIncluded}
            onChange={(e) => onIncludedChange?.(e.target.checked)}
          />
          Include in report
        </label>
      </div>
      {isCommercial && discrepancy !== null && (
        <p className="flagItem__meta">
          GC Billed: {gcBilledPct}% &nbsp;·&nbsp; CSC Field Estimate:{" "}
          {parseFloat(cscEstimation!)}% &nbsp;·&nbsp; Discrepancy: ~
          {discrepancy} percentage points
        </p>
      )}
      <FormField
        label="Flag Narrative for Report"
        required
        as="textarea"
        rows={4}
        value={narrative}
        onChange={onNarrativeChange}
        className="flagInput"
        error={error}
      />
    </div>
  );
};

const Step4FlagsNarrative = ({
  prefill,
  initialExtraFlags,
  onExtraFlagsChange,
  onFlagNarrativesChange,
  onFlagIncludesChange,
  submitted = false,
}: Props) => {
  const snapshot = prefill?.inspectionSnapshot;
  const phases = snapshot?.phases ?? [];
  const snapshotLineItems = snapshot?.lineItems ?? [];
  const rootRef = useRef<HTMLDivElement>(null);

  type NormalizedItem = {
    id: string;
    itemLabel: string;
    itemName?: string;
    itemCode?: number;
    phase?: string;
    cscEstimation: string;
    gcBilledPct?: number;
    isIncludedFlagInReport?: boolean;
    note: Record<string, string>;
  };

  const flaggedItems: NormalizedItem[] =
    phases.length > 0
      ? phases.flatMap((phase) =>
          phase.items
            .filter((item) => item.isFlagged)
            .map(
              (item): NormalizedItem => ({
                id: item.id,
                itemLabel: item.itemLabel,
                phase: phase.phase,
                cscEstimation: item.cscEstimation,
                isIncludedFlagInReport: item.isIncludedFlagInReport,
                note: item.note,
              }),
            ),
        )
      : snapshotLineItems
          .filter((item) => item.isFlagged)
          .map(
            (item): NormalizedItem => ({
              id: item.id,
              itemLabel: item.aiaItem.itemLabel,
              itemName: item.aiaItem.itemName,
              itemCode: item.aiaItem.itemCode,
              cscEstimation: item.cscEstimation,
              gcBilledPct: item.gcBilledPct,
              isIncludedFlagInReport: item.isIncludedFlagInReport,
              note: item.note,
            }),
          );

  const itemMap = new Map<string, NormalizedItem>(
    phases.length > 0
      ? phases.flatMap((phase) =>
          phase.items.map((item): [string, NormalizedItem] => [
            item.id,
            {
              id: item.id,
              itemLabel: item.itemLabel,
              phase: phase.phase,
              cscEstimation: item.cscEstimation,
              note: item.note,
            },
          ]),
        )
      : snapshotLineItems.map((item): [string, NormalizedItem] => [
          item.id,
          {
            id: item.id,
            itemLabel: item.aiaItem.itemLabel,
            itemName: item.aiaItem.itemName,
            itemCode: item.aiaItem.itemCode,
            cscEstimation: item.cscEstimation,
            gcBilledPct: item.gcBilledPct,
            note: item.note,
          },
        ]),
  );

  const [flagNarratives, setFlagNarratives] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        flaggedItems.map((item) => [item.id, item.note?.flag ?? ""]),
      ),
  );
  const [flagIncludes, setFlagIncludes] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        flaggedItems.map((item) => [
          item.id,
          item.isIncludedFlagInReport ?? true,
        ]),
      ),
  );
  const [extraFlags, setExtraFlags] = useState<ExtraFlag[]>(
    initialExtraFlags ?? [],
  );

  useEffect(() => {
    onFlagNarrativesChange?.(flagNarratives);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagNarratives]);

  useEffect(() => {
    onFlagIncludesChange?.(flagIncludes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagIncludes]);

  useEffect(() => {
    onExtraFlagsChange?.(extraFlags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraFlags]);

  useEffect(() => {
    if (submitted) {
      rootRef.current
        ?.querySelector<HTMLElement>(".field-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const [selectedId, setSelectedId] = useState("");
  const [selectError, setSelectError] = useState("");
  const [narrativeError, setNarrativeError] = useState("");
  const [gcCscText, setGcCscText] = useState("");
  const [manualNarrative, setManualNarrative] = useState("");
  const selectFieldRef = useRef<HTMLDivElement>(null);
  const narrativeRef = useRef<HTMLDivElement>(null);

  const addedIds = new Set([
    ...flaggedItems.map((f) => f.id),
    ...extraFlags.map((f) => f.id),
  ]);

  const nonFlaggedOptions =
    phases.length > 0
      ? phases.flatMap((phase) =>
          phase.items
            .filter((item) => !item.isFlagged && !addedIds.has(item.id))
            .map((item) => ({
              value: item.id,
              label: `Phase ${phase.phase} — ${item.itemLabel}`,
            })),
        )
      : snapshotLineItems
          .filter((item) => !item.isFlagged && !addedIds.has(item.id))
          .map((item) => ({
            value: item.id,
            label: `${item.aiaItem.itemName} (Line ${item.aiaItem.itemCode})`,
          }));

  const handleSelectItem = (value: string) => {
    setSelectedId(value);
    if (value) setSelectError("");
    const found = itemMap.get(value);
    setGcCscText(found ? `CSC: ${parseFloat(found.cscEstimation)}%` : "");
  };

  const handleAddItem = () => {
    const noSelect = !selectedId;
    const noNarrative = !manualNarrative.trim();
    setSelectError(noSelect ? "Line Item / Subject is required." : "");
    setNarrativeError(noNarrative ? "Narrative is required." : "");
    if (noSelect || noNarrative) {
      setTimeout(() => {
        (noSelect
          ? selectFieldRef.current
          : narrativeRef.current
        )?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
      return;
    }
    const found = itemMap.get(selectedId);
    if (!found) return;
    setExtraFlags((prev) => [
      ...prev,
      {
        id: found.id,
        phase: found.phase,
        itemLabel: found.itemLabel,
        itemName: found.itemName,
        itemCode: found.itemCode,
        cscEstimation: found.cscEstimation,
        gcBilledPct: found.gcBilledPct,
        gcCscText,
        narrative: manualNarrative,
        isIncluded: true,
      },
    ]);
    setSelectedId("");
    setGcCscText("");
    setManualNarrative("");
    setNarrativeError("");
  };
  const isCommercial = prefill?.project?.type === "commercial";
  return (
    <div className="craScreen" ref={rootRef}>
      <SectionHeader
        title="Overbilling Flags and Narrative"
        subtitle="Flagged items from FI are pre-populated. Edit narrative language for the report."
      />

      {flaggedItems.length === 0 && extraFlags.length === 0 && (
        <SourceBanner
          icon="✅"
          title="No Flags"
          text="No flagged items were raised in the field inspection."
        />
      )}

      {flaggedItems.map((item, index) => (
        <FlagCard
          key={item.id}
          index={index}
          phase={item.phase}
          itemLabel={item.itemLabel}
          itemName={item.itemName}
          itemCode={item.itemCode}
          gcBilledPct={item.gcBilledPct}
          cscEstimation={item.cscEstimation}
          narrative={flagNarratives[item.id] ?? ""}
          onNarrativeChange={(val) =>
            setFlagNarratives((prev) => ({ ...prev, [item.id]: val }))
          }
          isIncluded={flagIncludes[item.id] ?? true}
          onIncludedChange={(val) =>
            setFlagIncludes((prev) => ({ ...prev, [item.id]: val }))
          }
          error={
            submitted && !(flagNarratives[item.id] ?? "").trim()
              ? "Flag Narrative for Report is required"
              : ""
          }
          isCommercial={isCommercial}
        />
      ))}

      {extraFlags.map((flag, index) => (
        <FlagCard
          key={flag.id}
          index={flaggedItems.length + index}
          phase={flag.phase}
          itemLabel={flag.itemLabel}
          itemName={flag.itemName}
          itemCode={flag.itemCode}
          gcBilledPct={flag.gcBilledPct}
          cscEstimation={flag.cscEstimation}
          narrative={flag.narrative}
          isIncluded={flag.isIncluded}
          onIncludedChange={(val) =>
            setExtraFlags((prev) =>
              prev.map((f) =>
                f.id === flag.id ? { ...f, isIncluded: val } : f,
              ),
            )
          }
          isCommercial={isCommercial}
        />
      ))}
      <ResidentialEstimateNote isCommercial={isCommercial} />
      <Card title="Add Additional Note or Flag">
        <div className="craFieldRow cols2" style={{ marginBottom: 14 }}>
          <div
            className={`craField${selectError ? " selectHasError" : ""}`}
            ref={selectFieldRef}
          >
            <label>
              Line Item / Subject<span className="req">*</span>
            </label>
            <SearchableSelect
              options={nonFlaggedOptions}
              value={selectedId}
              onChange={(value) => handleSelectItem(value)}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="Select a line item..."
              error={!!selectError}
            />
            {selectError && <span className="field-error">{selectError}</span>}
          </div>
          <div className="craField">
            <label>GC Billed % · CSC Estimate %</label>
            <Input
              disabled
              type="text"
              className=""
              placeholder="e.g. 60% billed · CSC: 55% estimated"
              value={gcCscText}
              onChange={(e) => setGcCscText(e.target.value)}
            />
          </div>
        </div>
        <div
          className="craField"
          style={{ marginBottom: 14 }}
          ref={narrativeRef}
        >
          <label>
            Narrative<span className="req">*</span>
          </label>
          <TextArea
            rows={3}
            placeholder="Write the narrative for this item..."
            value={manualNarrative}
            onChange={(e) => {
              setManualNarrative(e.target.value);
              if (narrativeError) setNarrativeError("");
            }}
            className={narrativeError ? "error-input-red" : ""}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          {narrativeError && <p className="field-error">{narrativeError}</p>}
        </div>
        <div className="mt12">
          <Button className="btnSecondary" onClick={handleAddItem}>
            + Add Item
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Step4FlagsNarrative;
